import React, { useState, useEffect, useCallback, useContext } from 'react';
import InputNumberFormat from 'react-number-format';

import { Plus as PlusIcon } from '@styled-icons/bootstrap/Plus';
import { TrashAlt as TrashAltIcon } from '@styled-icons/boxicons-solid/TrashAlt';
import { Warning as WarningIcon } from '@styled-icons/fluentui-system-filled/Warning';
import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalFooter,
  Text,
  Icon,
  Tooltip,
  Box,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  IconButton,
  HStack,
} from '@chakra-ui/react';

import Loader from 'components/Loader';
import { ModalRootProps } from 'components/Modal/Root';
import OwnSelect from 'components/OwnSelect';

import {
  Base,
  Version,
  PriceType,
  Locale,
} from 'pages/Budget/Add/SubPages/Configuration/types';

import AuthContext from 'contexts/AuthContext';

import useModal from 'hooks/useModal';

type BaseInfo = {
  [id: string]: {
    versions: Version[];
    locales: Locale[];
    priceTypes: PriceType[];
  };
};

type SelectedInfo = {
  [id: string]: {
    resource_id?: number; // for update and delete
    version_id: number;
    locale_id: number;
    price_type_id: number;
    social_law?: number;
  };
};

type LoadingInfo = {
  [id: string]: boolean;
};

interface ModalBudgetBaseAddProps extends ModalRootProps {
  data: {
    budgetId: number;
    budgetDescription: string;
  };
}

function isSuccess<T>(
  r: PromiseSettledResult<T>,
): r is PromiseFulfilledResult<T> {
  return r.status === 'fulfilled';
}

const OWN_BASE_KEY_PREFIX = 'own.';

const ModalBudgetBaseAdd: React.FC<ModalBudgetBaseAddProps> = ({
  data: { budgetId, budgetDescription },
  onConfirm,
  handleClose,
  ...restProps
}) => {
  const { user } = useContext(AuthContext);
  const { openModal } = useModal();

  const [bases, setBases] = useState<Base[]>([]);
  const [budgetBases, setBudgetBases] = useState<Base[]>([]);

  const [baseInfo, setBaseInfo] = useState({} as BaseInfo);
  const [selectedBaseInfo, setSelectedBaseInfo] = useState({} as SelectedInfo);
  const [isFindingInfo, setIsFindingInfo] = useState({} as LoadingInfo);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const getBudget = useCallback(async () => {
    setLoading(true);

    try {
      const { data: budgetResponse } = await api.get(`/budget/${budgetId}`, {
        params: {
          organization_id: user.managed_organization.organization.id,
        },
      });

      const budgetData = budgetResponse.data;
      setBudgetBases(budgetData.bases);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao tentar carregar o orçamento',
        status: 'error',
      });

      setBudgetBases([]);
    } finally {
      setLoading(false);
    }
  }, [user, budgetId]);

  useEffect(() => {
    getBudget();
  }, [getBudget]);

  const getBaseInfoRequests = useCallback((baseId: number, baseKey: string) => {
    const versionSort = baseKey.startsWith(OWN_BASE_KEY_PREFIX)
      ? `-created_at`
      : `-pointer`;

    return ['version/list', 'locale', 'price/type'].map((subRoute) =>
      api.get(subRoute, {
        params: {
          'filter[status]': 'active',
          'filter[base_id]': baseId,
          sort: subRoute === 'version/list' ? versionSort : 'description',
        },
      }),
    );
  }, []);

  const getBaseInfo = useCallback(
    async (baseId: number, baseKey: string): Promise<void> => {
      setIsFindingInfo((oldInfo) => ({ ...oldInfo, [baseId]: true }));

      const requests = getBaseInfoRequests(baseId, baseKey);
      const results = await Promise.allSettled(requests);

      if (results.some((result) => result.status === 'rejected')) {
        toast({
          description: 'Houve um erro ao buscar informações da base.',
          status: 'error',
        });

        setIsFindingInfo((oldInfo) => ({ ...oldInfo, [baseId]: false }));
        return;
      }

      const [{ data: versions }, { data: locales }, { data: priceTypes }] =
        results.filter(isSuccess).map((result) => result.value);

      if (versions.length === 1) {
        const [version] = versions;
        setSelectedBaseInfo((oldInfo) => {
          const infoObj = oldInfo[baseId];

          return {
            ...oldInfo,
            [baseId]: {
              ...infoObj,
              version_id: version.id,
            },
          };
        });
      }

      if (locales.data.length === 1) {
        const [locale] = locales.data;

        setSelectedBaseInfo((oldInfo) => {
          const infoObj = oldInfo[baseId];
          return {
            ...oldInfo,
            [baseId]: {
              ...infoObj,
              locale_id: locale.id,
            },
          };
        });
      }

      if (priceTypes.data.length === 1) {
        const [priceType] = priceTypes.data;

        setSelectedBaseInfo((oldInfo) => {
          const infoObj = oldInfo[baseId];
          return {
            ...oldInfo,
            [baseId]: {
              ...infoObj,
              price_type_id: priceType.id,
            },
          };
        });
      }

      setBaseInfo((oldInfo) => {
        return {
          ...oldInfo,
          [baseId]: {
            versions,
            locales: locales.data,
            priceTypes: priceTypes.data,
          },
        };
      });

      setIsFindingInfo((oldInfo) => ({ ...oldInfo, [baseId]: false }));
    },
    [getBaseInfoRequests],
  );

  const handleAddBase = useCallback(
    (base: Base): void => {
      if (baseInfo[base.id]) {
        toast({ description: 'Base já selecionada.', status: 'error' });
        return;
      }

      setBases((oldBases) => [
        ...oldBases,
        {
          ...base,
          inUi: true,
        },
      ]);

      getBaseInfo(base.id, base.key);
    },
    [baseInfo, getBaseInfo],
  );

  useEffect(() => {
    if (budgetBases.length > 0) {
      const mappedBases = budgetBases.map((base) => ({
        resource_id: base.id,
        id: base.version.base.id,
        description: base.version.base.description,
        key: base.version.base.key,
        use_social_law: base.version.base.use_social_law,
        social_law: base.social_law,
        version: {
          id: base.version.id,
          description: base.version.description,
        },
        locale: {
          id: base.locale.id,
          description: base.locale.description,
        },
        price_type: {
          id: base.price_type.id,
          description: base.price_type.description,
        },
      })) as Base[];

      setBases((oldBases) =>
        oldBases.filter((base) => base.inUi).concat(mappedBases),
      );

      setSelectedBaseInfo((oldInfo) => {
        const newInfo = { ...oldInfo };

        mappedBases.forEach((base) => {
          newInfo[base.id] = {
            version_id: base.version.id,
            locale_id: base.locale.id,
            price_type_id: base.price_type.id,
            resource_id: base.resource_id,
          };
        });

        return newInfo;
      });

      mappedBases.forEach((base) => {
        getBaseInfo(base.id, base.key);
      });
    }
  }, [budgetBases, getBaseInfo]);

  const baseVersionsToSelect = useCallback(
    (baseId: number) => {
      if (!baseInfo[baseId]) return [];

      return baseInfo[baseId].versions.map((version) => ({
        value: version.id,
        label: version.description,
      }));
    },
    [baseInfo],
  );

  const baseLocalesToSelect = useCallback(
    (baseId: number) => {
      if (!baseInfo[baseId]) return [];

      return baseInfo[baseId].locales.map((locale) => ({
        value: locale.id,
        label: locale.description,
      }));
    },
    [baseInfo],
  );

  const basePriceTypesToSelect = useCallback(
    (baseId: number) => {
      if (!baseInfo[baseId]) return [];

      return baseInfo[baseId].priceTypes.map((locale) => ({
        value: locale.id,
        label: locale.description,
      }));
    },
    [baseInfo],
  );

  const handleRemoveBaseFromUI = useCallback(
    (baseId: number) => {
      const newBases = bases.filter((b) => b.id !== baseId);

      setBases(newBases);
      delete baseInfo[baseId];
      delete selectedBaseInfo[baseId];
    },
    [bases, baseInfo, selectedBaseInfo],
  );

  const handleSubmit = async (): Promise<void> => {
    if (!budgetId) {
      toast({ description: 'Orçamento não configurado!', status: 'error' });
      return;
    }

    setSaving(true);

    const selectedInUiBases: SelectedInfo = {};

    bases.forEach((base) => {
      if (base.inUi) {
        selectedInUiBases[base.id] = selectedBaseInfo[base.id];
      }
    });

    const selectedUiInfo = Array.from(Object.entries(selectedInUiBases)).map(
      ([, { version_id, locale_id, price_type_id, social_law }]) => {
        const hasSocialLaw = social_law !== null && social_law !== undefined;
        return {
          version_id,
          locale_id,
          price_type_id,
          ...(hasSocialLaw && { social_law }),
        };
      },
    );

    try {
      const updateRequests = [];

      if (selectedUiInfo.length > 0) {
        updateRequests.push(
          api.post(`/budget/${budgetId}/base`, {
            configs: selectedUiInfo,
          }),
        );
      }

      await Promise.all(updateRequests);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao tentar salvar as configurações da base!',
        status: 'error',
      });
    }

    setSaving(false);
  };

  const handleConfirm = async (): Promise<void> => {
    await handleSubmit();
    if (onConfirm) onConfirm();
    handleClose();
  };

  return (
    <Modal size="6xl" {...restProps}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Adicionar bases no orçamento</ModalHeader>

        <ModalHeader
          mt="-6"
          fontSize="md"
          fontStyle="italic"
          display="flex"
          gap={2}
          flexWrap="wrap"
        >
          <Text>Orçamento: {budgetDescription}</Text>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <Box
            w="100%"
            display="flex"
            flexDirection={{ base: 'column', sm: 'row' }}
            alignItems={{ base: 'flex-start', sm: 'center' }}
            justifyContent={{ base: 'flex-start', lg: 'flex-start' }}
            gridGap="1"
          >
            <Button
              size="sm"
              colorScheme="green"
              w={{ base: '100%', md: 'unset' }}
              onClick={() =>
                openModal('baseSelect', {
                  mode: 'select',
                  onConfirm: handleAddBase,
                })
              }
              leftIcon={<Icon as={PlusIcon} />}
            >
              Adicionar base de dados
            </Button>

            <Tooltip
              hasArrow
              label={
                <Box d="flex" flexDirection="column" gridGap={2}>
                  <Text>
                    🔴{' '}
                    <Text as="span" fontWeight="bold">
                      Atenção:
                    </Text>{' '}
                    Para garantir a precisão e o funcionamento correto do
                    orçamento, utilize bases com a mesma referência.
                  </Text>

                  <Text>
                    ⚠️ Misturar bases com referências diferentes pode gerar
                    inconsistências nos valores e afetar a importação e
                    integração dos dados.
                  </Text>
                </Box>
              }
            >
              <Icon
                as={WarningIcon}
                w={8}
                h={8}
                className="icon"
                color="orange.500"
                style={{ cursor: 'help' }}
              />
            </Tooltip>
          </Box>

          <Box mt={8}>
            {bases.length === 0 && budgetBases.length === 0 && loading && (
              <Loader />
            )}

            {bases.length > 0 &&
              bases.map((base) => (
                <SimpleGrid
                  key={base.id}
                  columns={{
                    base: 1,
                    md: base.use_social_law ? 6 : 5,
                  }}
                  marginTop={4}
                  gap={2}
                >
                  <FormControl>
                    <FormLabel>Base de dados</FormLabel>

                    <Input
                      type="text"
                      value={base.description}
                      isReadOnly
                      _readOnly={{ background: 'white' }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Referência</FormLabel>

                    <OwnSelect
                      placeholder={
                        isFindingInfo[base.id] ? 'Carregando' : 'Selecione'
                      }
                      options={baseVersionsToSelect(base.id)}
                      value={baseVersionsToSelect(base.id).filter(
                        (s) =>
                          s.value === selectedBaseInfo[base.id]?.version_id,
                      )}
                      isLoading={isFindingInfo[base.id]}
                      isDisabled={
                        baseVersionsToSelect(base.id).length === 0 || !base.inUi
                      }
                      onChange={(selected) => {
                        const infoObj = selectedBaseInfo[base.id];

                        setSelectedBaseInfo((oldInfo) => ({
                          ...oldInfo,
                          [base.id]: {
                            ...infoObj,
                            version_id: Number(selected?.value),
                          },
                        }));
                      }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Estado</FormLabel>

                    <OwnSelect
                      placeholder={
                        isFindingInfo[base.id] ? 'Carregando' : 'Selecione'
                      }
                      options={baseLocalesToSelect(base.id)}
                      value={baseLocalesToSelect(base.id).filter(
                        (s) => s.value === selectedBaseInfo[base.id]?.locale_id,
                      )}
                      isLoading={isFindingInfo[base.id]}
                      isDisabled={
                        baseLocalesToSelect(base.id).length === 0 || !base.inUi
                      }
                      onChange={(selected) => {
                        const infoObj = selectedBaseInfo[base.id];

                        setSelectedBaseInfo((oldInfo) => ({
                          ...oldInfo,
                          [base.id]: {
                            ...infoObj,
                            locale_id: Number(selected?.value),
                          },
                        }));
                      }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Tipo de preço</FormLabel>

                    <OwnSelect
                      placeholder={
                        isFindingInfo[base.id] ? 'Carregando' : 'Selecione'
                      }
                      options={basePriceTypesToSelect(base.id)}
                      value={basePriceTypesToSelect(base.id).filter(
                        (s) =>
                          s.value === selectedBaseInfo[base.id]?.price_type_id,
                      )}
                      isLoading={isFindingInfo[base.id]}
                      isDisabled={
                        basePriceTypesToSelect(base.id).length === 0 ||
                        !base.inUi
                      }
                      onChange={(selected) => {
                        const infoObj = selectedBaseInfo[base.id];

                        setSelectedBaseInfo((oldInfo) => ({
                          ...oldInfo,
                          [base.id]: {
                            ...infoObj,
                            price_type_id: Number(selected?.value),
                          },
                        }));
                      }}
                    />
                  </FormControl>

                  {base.use_social_law && (
                    <FormControl>
                      <FormLabel>Leis sociais</FormLabel>
                      <InputNumberFormat
                        type="tel"
                        customInput={Input}
                        displayType="input"
                        className="text-right"
                        placeholder="0 %"
                        fixedDecimalScale
                        thousandSeparator="."
                        decimalSeparator=","
                        suffix=" %"
                        decimalScale={2}
                        defaultValue={base.social_law}
                        onValueChange={(v) => {
                          const infoObj = selectedBaseInfo[base.id];
                          setSelectedBaseInfo((oldInfo) => ({
                            ...oldInfo,
                            [base.id]: {
                              ...infoObj,
                              social_law: v.floatValue || 0,
                            },
                          }));
                        }}
                        isDisabled={!base.inUi}
                        style={{
                          color: base.inUi ? 'inherit' : 'hsl(0, 0%, 60%)',
                          borderColor: base.inUi
                            ? 'inherit'
                            : 'hsl(0, 0%, 87%)',
                        }}
                      />
                    </FormControl>
                  )}

                  {base.inUi && (
                    <FormControl>
                      <FormLabel>Remover base</FormLabel>

                      <IconButton
                        size="md"
                        aria-label="Remover base do orçamento"
                        title="Remover base do orçamento"
                        colorScheme="red"
                        width={{ base: '100%', md: 'unset' }}
                        onClick={() => {
                          handleRemoveBaseFromUI(base.id);
                        }}
                      >
                        <Icon as={TrashAltIcon} />
                      </IconButton>
                    </FormControl>
                  )}
                </SimpleGrid>
              ))}
          </Box>
        </ModalBody>

        <ModalFooter>
          <HStack gap={1}>
            <Button
              isDisabled={loading}
              colorScheme="gray"
              onClick={handleClose}
              disabled={loading || saving}
            >
              Cancelar
            </Button>

            <Button
              isDisabled={loading}
              colorScheme="green"
              onClick={handleConfirm}
              disabled={loading || saving}
            >
              Confirmar alterações
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalBudgetBaseAdd;
